import { ensureOk } from "@/api/apiError";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { components } from "@/api/generated-types";
import type { AdminAgent, AdminAgentCreate, AdminAgentUpdate } from "@/types";
import type { AdminListQuery } from "./useAdminUsers";

export type PaginatedAdminAgents =
  components["schemas"]["PaginatedResponse_AdminAgent_"];

function buildQueryString(params: AdminListQuery): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  search.set("skip", String(params.skip ?? 0));
  search.set("limit", String(params.limit ?? 20));
  return `?${search.toString()}`;
}

async function fetchAdminAgents(
  params: AdminListQuery,
): Promise<PaginatedAdminAgents> {
  const response = await httpClient(`/admin/agents${buildQueryString(params)}`);
  await ensureOk(response);
  return response.json() as Promise<PaginatedAdminAgents>;
}

async function fetchAdminAgent(slug: string): Promise<AdminAgent> {
  const response = await httpClient(
    `/admin/agents/${encodeURIComponent(slug)}`,
  );
  await ensureOk(response);
  return response.json() as Promise<AdminAgent>;
}

/** US-11: admin listing of all agents (any status), search + pagination server-side. */
export function useAdminAgents(params: AdminListQuery = {}) {
  return useQuery({
    queryKey: ["admin", "agents", params],
    queryFn: () => fetchAdminAgents(params),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAdminAgent(slug: string | undefined) {
  return useQuery({
    queryKey: ["admin", "agents", slug],
    queryFn: () => fetchAdminAgent(slug as string),
    enabled: Boolean(slug),
    staleTime: 30 * 1000,
  });
}

async function createAdminAgent(input: AdminAgentCreate): Promise<AdminAgent> {
  const response = await httpClient("/admin/agents", {
    method: "POST",
    body: JSON.stringify(input),
  });
  await ensureOk(response);
  return response.json() as Promise<AdminAgent>;
}

async function updateAdminAgent(input: {
  slug: string;
  patch: AdminAgentUpdate;
}): Promise<AdminAgent> {
  const response = await httpClient(
    `/admin/agents/${encodeURIComponent(input.slug)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input.patch),
    },
  );
  await ensureOk(response);
  return response.json() as Promise<AdminAgent>;
}

async function deleteAdminAgent(slug: string): Promise<void> {
  const response = await httpClient(
    `/admin/agents/${encodeURIComponent(slug)}`,
    {
      method: "DELETE",
    },
  );
  await ensureOk(response);
}

/** Creates an agent (and its auto-created usage/validator groups). Credentials are write-only
 * (never returned in clear text — `hasApiKey`/`hasOAuthClientId`/`hasOAuthClientSecret` booleans
 * only). */
export function useCreateAdminAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAdminAgent,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "agents"] });
    },
  });
}

export function useUpdateAdminAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAdminAgent,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.slug],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "agents"] });
    },
  });
}

export function useDeleteAdminAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAdminAgent,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "agents"] });
    },
  });
}
